import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Download, Loader2 } from "lucide-react";
import clsx from "clsx";
import { track } from "../../lib/tracking";
import DatenschutzCheckbox from "./DatenschutzCheckbox";

const LeadSchema = z.object({
  name: z.string().min(2, "Name muss mindestens 2 Zeichen haben."),
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein."),
  telefon: z.string().optional(),
  datenschutz: z.literal(true, {
    errorMap: () => ({ message: "Bitte stimmen Sie der Datenschutzerklärung zu." }),
  }),
});

type LeadForm = z.infer<typeof LeadSchema>;

interface PDFDownloadGateProps {
  tool: "foerder-rechner" | "roi-simulator" | "dach-mapper";
  eingabedaten: Record<string, unknown>;
  ergebnis: Record<string, unknown>;
  onSuccess?: (leadId: string) => void;
}

export default function PDFDownloadGate({
  tool,
  eingabedaten,
  ergebnis,
  onSuccess,
}: PDFDownloadGateProps) {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { register, handleSubmit, formState: { errors } } = useForm<LeadForm>({
    resolver: zodResolver(LeadSchema),
  });

  const onSubmit = async (data: LeadForm) => {
    setLoading(true);
    setError(null);

    try {
      // Tenant slug from the render-route-injected meta tag — required by the backend
      // tenantResolver middleware on /api/{tool}/* (see backend/api/shared/tenant-resolver.ts).
      const tenantSlug =
        document.querySelector<HTMLMetaElement>('meta[name="x-tenant-slug"]')?.content ?? "";
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (tenantSlug) headers["x-tenant-slug"] = tenantSlug;

      const res = await fetch(`/api/${tool}/generate-pdf`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          tool,
          name: data.name,
          email: data.email,
          telefon: data.telefon || undefined,
          eingabedaten,
          ergebnis,
          quelle: window.location.href,
          consent_given: true, // data.datenschutz is z.literal(true) — guaranteed if onSubmit fires
        }),
      });

      if (!res.ok) {
        const blob = await res.blob();
        const text = await blob.text();
        let msg = "Fehler beim PDF-Download.";
        try { msg = JSON.parse(text).fehler ?? msg; } catch { /* ignore */ }
        throw new Error(msg);
      }

      // PDF als Blob empfangen und direkt triggern
      const pdfBlob = await res.blob();
      const url = URL.createObjectURL(pdfBlob);
      const datum = new Date().toISOString().slice(0, 10);
      const nameSlug = data.name.replace(/\s+/g, "_");
      const fileNames: Record<string, string> = {
        "foerder-rechner": `NEKO_Foerdercheck_${nameSlug}_${datum}.pdf`,
        "roi-simulator": `NEKO_PV_Berechnung_${nameSlug}_${datum}.pdf`,
        "dach-mapper": `NEKO_Dach_Analyse_${nameSlug}_${datum}.pdf`,
      };
      const fileName = fileNames[tool];

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      setSubmitted(true);
      track("generate_lead", { tool, method: "pdf_download" });

      // Phase 6 / OPS-03: cookieless conversion event — only after confirmed success.
      // Properties carry NO PII — only tenant_id and tool. tenant_id read from meta tag (Phase 3 pattern).
      if (typeof window !== "undefined" && typeof window.plausible === "function") {
        const slugMeta = document.querySelector('meta[name="x-tenant-slug"]');
        const tenantId = slugMeta?.getAttribute("content") ?? "unknown";
        window.plausible("lead_submitted", {
          props: { tenant_id: tenantId, tool },
        });
      }

      onSuccess?.(fileName);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unbekannter Fehler.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="bg-neko-green/10 border border-neko-green/30 rounded-xl p-4 text-center animate-fade-in"
        role="status"
        aria-live="polite"
        data-testid="pdf-success"
      >
        <div className="text-2xl mb-2">✓</div>
        <p className="text-neko-white font-medium">PDF wird heruntergeladen!</p>
        <p className="text-neko-gray text-sm mt-1">Wir melden uns bald bei Ihnen.</p>
      </div>
    );
  }

  return (
    <div className="bg-neko-dark-2 border border-neko-dark-3 rounded-xl p-4 animate-fade-in">
      <div className="flex items-center gap-2 mb-3">
        <Download className="w-5 h-5 text-neko-orange" />
        <h3 className="text-neko-white font-medium">Ergebnis als PDF herunterladen</h3>
      </div>
      <p className="text-neko-gray text-sm mb-4">
        Kostenlos – wir senden Ihnen das PDF zu und melden uns für ein unverbindliches Beratungsgespräch.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className="mb-3">
          <label htmlFor="pdf-name" className="block text-sm text-neko-gray-light mb-1">Name *</label>
          <input
            id="pdf-name"
            type="text"
            autoComplete="name"
            placeholder="Max Mustermann"
            className={clsx(
              "w-full bg-neko-dark border rounded-lg px-3 py-2.5 text-neko-white text-base",
              "placeholder-neko-gray focus:outline-none focus:ring-2 transition-all",
              errors.name
                ? "border-neko-red focus:ring-neko-red/40"
                : "border-neko-dark-3 focus:ring-neko-orange/40 focus:border-neko-orange"
            )}
            {...register("name")}
            data-testid="pdf-name-input"
          />
          {errors.name && <p className="text-xs text-neko-red mt-1" role="alert">{errors.name.message}</p>}
        </div>

        <div className="mb-3">
          <label htmlFor="pdf-email" className="block text-sm text-neko-gray-light mb-1">E-Mail *</label>
          <input
            id="pdf-email"
            type="email"
            autoComplete="email"
            placeholder="max@example.com"
            className={clsx(
              "w-full bg-neko-dark border rounded-lg px-3 py-2.5 text-neko-white text-base",
              "placeholder-neko-gray focus:outline-none focus:ring-2 transition-all",
              errors.email
                ? "border-neko-red focus:ring-neko-red/40"
                : "border-neko-dark-3 focus:ring-neko-orange/40 focus:border-neko-orange"
            )}
            {...register("email")}
            data-testid="pdf-email-input"
          />
          {errors.email && <p className="text-xs text-neko-red mt-1" role="alert">{errors.email.message}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="pdf-telefon" className="block text-sm text-neko-gray mb-1">
            Telefon <span className="font-normal">(optional)</span>
          </label>
          <input
            id="pdf-telefon"
            type="tel"
            autoComplete="tel"
            placeholder="+49 173 1234567"
            className="w-full bg-neko-dark/70 border border-neko-dark-3/60 rounded-lg px-3 py-2.5 text-neko-white text-base placeholder-neko-gray focus:outline-none focus:ring-2 focus:ring-neko-orange/40 focus:border-neko-orange transition-all"
            {...register("telefon")}
          />
        </div>

        <DatenschutzCheckbox register={register} error={errors.datenschutz} />

        {error && (
          <p className="text-xs text-neko-red mb-3" role="alert">{error}</p>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full bg-neko-orange hover:bg-neko-orange-dark text-neko-dark font-bold py-3 px-4 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="pdf-submit-btn"
        >
          {loading ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Wird vorbereitet…</>
          ) : (
            <><Download className="w-4 h-4" /> PDF kostenlos herunterladen</>
          )}
        </button>

        <p className="text-xs text-neko-gray mt-2 text-center">
          Kein Spam. Nur NEKO kontaktiert Sie – versprochen.
        </p>
      </form>
    </div>
  );
}
